import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaLinkedin, FaGithub, FaEnvelope, FaArrowDown, FaArrowRight, FaExpand } from 'react-icons/fa';
import { toneStyle } from '../data/projects';
import { useSite, scrollToSection } from '../SiteContext';
import { useI18n, useT } from '../i18n';
import { useContent } from '../i18n/useContent';
import { endYear } from '../i18n/format';
import CopyEmail from './CopyEmail';
import './Hero.css';

const getInitials = (title = '') => {
  const words = title.split(' ').map((w) => w.replace(/[^a-zA-Z0-9]/g, '')).filter(Boolean);
  if (words.length === 0) return 'HT';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Local time where I am — useful context for anyone hiring across timezones.
 *
 * The hour cycle is pinned to 24-hour rather than inferred from the reader's
 * locale: this is a readout of how South Africa writes time, not a value that
 * should flip to 12-hour AM/PM just because the page is being read in a
 * language that would otherwise default to it. Locale still governs digit
 * shaping and separators.
 */
const useLocalTime = (locale) => {
  const format = useCallback(
    () =>
      new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: 'Africa/Johannesburg',
      }).format(new Date()),
    [locale]
  );

  const [time, setTime] = useState(format);

  useEffect(() => {
    setTime(format());
    const id = setInterval(() => setTime(format()), 30000);
    return () => clearInterval(id);
  }, [format]);

  return time;
};

const Hero = () => {
  const { openProject } = useSite();
  const { lang } = useI18n();
  const { projects } = useContent();
  const t = useT();
  const [activeIndex, setActiveIndex] = useState(0);
  const [failed, setFailed] = useState({});
  const time = useLocalTime(lang);

  // Five rows plus the preview: enough to show range without the panel
  // towering over the nameplate column beside it.
  const indexEntries = useMemo(
    () => [...projects].sort((a, b) => Number(endYear(b)) - Number(endYear(a))).slice(0, 5),
    [projects]
  );

  const active = indexEntries[activeIndex];
  const image = active?.images?.[0];
  const showImage = image && !failed[image];

  return (
    <section className="hero">
      <div className="container">
        <div className="hero-grid">
          <div className="hero-lead">
            <p className="eyebrow hero-eyebrow">
              {t('hero.role')} <span aria-hidden="true">—</span> {t('hero.location')}
              <span className="hero-clock" aria-label={t('a11y.localTime', { time })}>
                <span className="hero-clock-dot" aria-hidden="true" />
                {time}
              </span>
            </p>

            <h1 className="hero-name">Herve Tusse</h1>

            <p className="hero-lede">
              {t('hero.lede')}
            </p>

            <div className="hero-actions">
              <button className="btn btn-primary" onClick={() => scrollToSection('projects')}>
                {t('hero.viewWork')}
                <FaArrowDown />
              </button>
              <a href={t('resume.file')} className="btn btn-ghost" download>
                {t('hero.downloadCv')}
              </a>
            </div>

            <div className="hero-contact">
              <span className="hero-contact-pair">
                <a href="mailto:hervetusse@gmail.com" className="hero-contact-item">
                  <FaEnvelope aria-hidden="true" />
                  hervetusse@gmail.com
                </a>
                <CopyEmail />
              </span>
              <a
                href="https://www.linkedin.com/in/herve-tusse-8a2479177/"
                className="hero-contact-item"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedin aria-hidden="true" />
                LinkedIn
              </a>
              <a
                href="https://github.com/htusse"
                className="hero-contact-item"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaGithub aria-hidden="true" />
                GitHub
              </a>
            </div>
          </div>

          {/* The index doubles as a browser: pointing at a row previews that
              project, and choosing one opens it. */}
          <aside className="hero-index" aria-label={t('hero.recentWork')}>
            <button
              type="button"
              className="index-preview"
              style={toneStyle(active?.category)}
              onClick={() => active && openProject(active)}
              aria-label={t('a11y.openProject', { title: active?.title || '' })}
            >
              <span className="index-preview-frame">
                {showImage ? (
                  <img
                    key={image}
                    src={image}
                    alt=""
                    className="index-preview-img"
                    onError={() => setFailed((f) => ({ ...f, [image]: true }))}
                  />
                ) : (
                  <span className="index-preview-fallback">{getInitials(active?.title)}</span>
                )}
              </span>

              <span className="index-preview-bar">
                <span className="index-preview-title">{active?.title}</span>
                <span className="index-preview-open">
                  <FaExpand aria-hidden="true" />
                </span>
              </span>
            </button>

            <div className="index-head">
              <span className="index-head-label">{t('hero.recentWork')}</span>
              <span className="index-head-note">{t('hero.totalCount', { count: projects.length })}</span>
            </div>

            <ol className="index-list">
              {indexEntries.map((project, index) => (
                <li key={project.title} style={{ '--i': index }}>
                  <button
                    type="button"
                    className={`index-row ${index === activeIndex ? 'is-active' : ''}`}
                    style={toneStyle(project.category)}
                    onMouseEnter={() => setActiveIndex(index)}
                    onFocus={() => setActiveIndex(index)}
                    onClick={() => openProject(project)}
                  >
                    <span className="index-year">{endYear(project)}</span>
                    <span className="index-title">{project.title}</span>
                    <span className="index-stack">{project.technologies[0]}</span>
                  </button>
                </li>
              ))}
            </ol>

            <button className="index-more" onClick={() => scrollToSection('projects')}>
              {t('hero.seeEvery')}
              <FaArrowRight aria-hidden="true" />
            </button>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Hero;
